import { useSchemaStatusSummary, useTimeSeriesTrend } from '../../../../../hooks/useDataQuality'
import { MetricType } from '../../../../../types/dataQuality'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import LoadingSkeleton from '../../../../common/LoadingSkeleton'
import ErrorDisplay from '../../../../common/ErrorDisplay'
import './SchemaStabilityChart.css'

const SchemaStabilityChart = () => {
  // Fetch time series data for schema stability (primary data source)
  const {
    data: timeSeriesData,
    isLoading: isTimeSeriesLoading,
    error: timeSeriesError
  } = useTimeSeriesTrend({
    metric_type: MetricType.SCHEMA_STABILITY_SCORE,
    days_back: 7,
    granularity: 'daily',
    include_trend_line: false
  })

  // Optionally fetch schema status summary for additional context
  const {
    data: schemaStatusData,
    isLoading: isSchemaLoading,
    error: schemaError
  } = useSchemaStatusSummary({})

  const isLoading = isTimeSeriesLoading || isSchemaLoading
  const error = timeSeriesError || schemaError

  if (isLoading) {
    return (
      <div className="schema-stability-section">
        <div className="section-header-compact">
          <h3 className="section-title-compact">Schema Stability Score</h3>
        </div>
        <LoadingSkeleton variant="chart" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="schema-stability-section">
        <ErrorDisplay
          error={error.message}
          onRetry={() => window.location.reload()}
          title="Failed to Load Schema Stability"
        />
      </div>
    )
  }

  // Check if we have time series data
  if (!timeSeriesData || (!timeSeriesData.summary && !timeSeriesData.chart_data)) {
    return (
      <div className="schema-stability-section">
        <div className="section-header-compact">
          <h3 className="section-title-compact">Schema Stability Score</h3>
          <p className="section-subtitle-compact">Field structure consistency over time</p>
        </div>
        <p className="no-data-message">
          No schema stability data available
          {timeSeriesData && <span style={{ display: 'block', fontSize: '11px', color: '#9ca3af', marginTop: '8px' }}>
            Received: {JSON.stringify(Object.keys(timeSeriesData))}. Check console for details.
          </span>}
        </p>
      </div>
    )
  }

  // Use average from time series data as primary source
  // Fallback to calculating from chart_data if summary is missing
  const stabilityScore = timeSeriesData.summary?.average_value ||
    (timeSeriesData.chart_data && timeSeriesData.chart_data.length > 0
      ? timeSeriesData.chart_data.reduce((sum, point) => sum + point.value, 0) / timeSeriesData.chart_data.length
      : 0)

  // Try to get health status from schema summary, or derive from score
  const overallHealth = schemaStatusData?.overall_health ||
    (stabilityScore >= 98 ? 'stable' : stabilityScore >= 95 ? 'drift' : 'volatile')

  // Format date to MM/DD
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return `${date.getMonth() + 1}/${date.getDate()}`
  }

  // Transform time series data for recharts (if available)
  const chartData = timeSeriesData?.chart_data?.map(point => ({
    date: formatDate(point.date),
    value: point.value,
    fullDate: point.date
  })) || []

  // Get status color based on stability score
  const getStatusColor = (score: number): string => {
    if (score >= 98) return '#10b981' // Green - Stable
    if (score >= 95) return '#F0B100' // Yellow - Drift
    return '#FB2C36' // Red - Volatile
  }

  // Get trend icon
  const getTrendIcon = () => {
    if (overallHealth === 'stable') {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5">
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
          <polyline points="17 6 23 6 23 12"/>
        </svg>
      )
    }
    return null
  }

  const statusColor = getStatusColor(stabilityScore)

  return (
    <div className="schema-stability-section">
      <div className="section-header-compact">
        <h3 className="section-title-compact">Schema Stability Score</h3>
        <p className="section-subtitle-compact">Field structure consistency over time</p>
      </div>

      <div className="schema-main-score">
        <div className="schema-score-row">
          <div className="schema-score-large">
            {stabilityScore.toFixed(1)}%
          </div>
          <span className="schema-status-text">
            {overallHealth}
          </span>
        </div>
        {getTrendIcon()}
      </div>

      {chartData.length > 0 && (
        <div className="schema-chart-container">
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart
              data={chartData}
              margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="colorSchemaStability" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={statusColor} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={statusColor} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                stroke="#9ca3af"
                style={{ fontSize: '12px' }}
                tick={{ fill: '#6b7280' }}
              />
              <YAxis
                domain={[95, 100]}
                stroke="#9ca3af"
                style={{ fontSize: '12px' }}
                tick={{ fill: '#6b7280' }}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '12px'
                }}
                formatter={(value: any) => typeof value === 'number' ? [`${value.toFixed(1)}%`, 'Stability'] : ['', '']}
                labelStyle={{ color: '#111827', fontWeight: 600 }}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke={statusColor}
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorSchemaStability)"
                animationDuration={1500}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="schema-legend">
        <div className="schema-legend-item">
          <div className="schema-legend-dot" style={{ backgroundColor: '#10b981' }}></div>
          <span className="schema-legend-label">≥ 98% Stable</span>
        </div>
        <div className="schema-legend-item">
          <div className="schema-legend-dot" style={{ backgroundColor: '#F0B100' }}></div>
          <span className="schema-legend-label">95-98% Drift</span>
        </div>
        <div className="schema-legend-item">
          <div className="schema-legend-dot" style={{ backgroundColor: '#FB2C36' }}></div>
          <span className="schema-legend-label">&lt; 95% Volatile</span>
        </div>
      </div>
    </div>
  )
}

export default SchemaStabilityChart
